import { useState, useEffect } from "react";
import { CreatorListData } from "./data";
import CreatorInvite from "../CreatorInvite/CreatorInvite";
import { showToast } from "../../utils/Toastify/Toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearch } from "../../context/SearchContext";
import ListView from "../../component/ListView/ListView";
import { creatorsDelete, creatorStatusChange } from "../../api/HttpApis";
import InputText from "../../component/inputText/InputText";
import { useRbac } from "../../rbac/RbacContext";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const CreatorList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creatorData, setCreatorData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const { searchQuery, setSearchQuery } = useSearch();
  const { can } = useRbac();

  const fetchCreatorList = async (limit, page) => {
    try {
      setLoading(true);
      const data = await CreatorListData(page, limit, searchQuery);
      setCreatorData(data);
      // Always sync the total — an empty page (e.g. after deleting the last
      // item) must reset it to 0 rather than keep the stale count.
      setTotalCount(
        Array.isArray(data) && data.length > 0 ? data[0].totalCount || 0 : 0
      );
    } catch (error) {
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) showToast(fieldErrors[0], "error");
        else if (response.message) showToast(response.message, "error");
      } else if (response?.message) {
        showToast(response.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (!confirmed || !id) return;
    setDeletingId(id);
    try {
      const response = await creatorsDelete(id);
      showToast(response.data.message, "success");
    } catch (error) {
      const response = error?.response?.data;
      showToast(response?.message || "Error deleting creator", "error");
    } finally {
      setDeletingId(null);
      fetchCreatorList();
    }
  };

  const handleStatusChange = async (id, nextStatus) => {
    const verb = nextStatus === "Inactive" ? "deactivate" : "reactivate";
    const confirmed = window.confirm(
      `Are you sure you want to ${verb} this creator?`
    );
    if (!confirmed || !id) return;
    setStatusChangingId(id);
    try {
      const response = await creatorStatusChange(id, nextStatus);
      showToast(response.data.message, "success");
    } catch (error) {
      const response = error?.response?.data;
      showToast(response?.message || `Error trying to ${verb} creator`, "error");
    } finally {
      setStatusChangingId(null);
      fetchCreatorList();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery === "" || searchQuery) {
        fetchCreatorList();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const cardFor = (item) => ({
    id: item.id,
    image: item.icon,
    thumbLabel: initials(item.name),
    title: item.name,
    meta: [item.email, `${item.contributions || 0} contributions`]
      .filter(Boolean)
      .join(" · "),
    actions: [
      ...(can("users", "edit") && (item.status === "Approved" || item.status === "Inactive")
        ? [
            item.status === "Inactive"
              ? {
                  icon: "accept",
                  label: "Activate",
                  tone: "success",
                  disabled: statusChangingId === item.id,
                  onClick: () => handleStatusChange(item.id, "Approved"),
                }
              : {
                  icon: "reject",
                  label: "Deactivate",
                  tone: "danger",
                  disabled: statusChangingId === item.id,
                  onClick: () => handleStatusChange(item.id, "Inactive"),
                },
          ]
        : []),
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === item.id,
        onClick: () => handleDelete(item.id),
      },
    ],
  });

  const stats = [
    { label: "Total Creators", value: (totalCount || 0).toLocaleString() },
  ];

  return (
    <div>
      <CreatorInvite
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInviteSuccess={fetchCreatorList}
      />

      <div style={{ width: 300, marginBottom: 16 }}>
        <InputText
          value={searchQuery || ""}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search creators..."
        />
      </div>

      <ListView
        heading="Creator List"
        rowsOnly
        data={creatorData}
        getCard={cardFor}
        stats={stats}
        totalCount={totalCount}
        onPageChange={fetchCreatorList}
        tableLoading={loading}
        addLabel="New Creator"
        onAdd={() => setIsModalOpen(true)}
      />
    </div>
  );
};

export default CreatorList;
